/**
 * @fileoverview Italian translations.
 * @summary Italian translation strings for KaraoNow frontend.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n/locales/it
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
export const it = {
  common: {
    loading: 'Caricamento...',
    processing: 'Elaborazione',
    error: 'Errore',
    success: 'Successo',
    cancel: 'Annulla',
    confirm: 'Conferma',
    save: 'Salva',
    delete: 'Elimina',
    edit: 'Modifica',
    create: 'Crea',
    back: 'Indietro',
    next: 'Successivo',
    previous: 'Precedente',
    search: 'Cerca',
    filter: 'Filtra',
    close: 'Chiudi',
    backHome: 'Torna alla home',
    creating: 'Creazione in corso...',
  },
  auth: {
    login: 'Accedi',
    register: 'Registrati',
    logout: 'Esci',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Conferma password',
    forgotPassword: 'Password dimenticata?',
    rememberMe: 'Ricordami',
    loginButton: 'Accedi',
    registerButton: 'Registrati',
    alreadyHaveAccount: 'Hai già un account?',
    dontHaveAccount: 'Non hai un account?',
    role: 'Ruolo',
    organizer: 'Organizzatore',
    singer: 'Cantante',
    firstName: 'Nome',
    lastName: 'Cognome',
    pseudo: 'Pseudonimo',
    organization: 'Organizzazione',
    sendVerificationEmail: 'Invia email di verifica',
    emailVerificationSent: 'Email di verifica inviata',
    emailVerificationInstructions: 'È stata inviata un\'email di verifica a {{email}}. Fai clic sul link nell\'email per completare la registrazione.',
    backToLogin: 'Torna al login',
    optional: 'opzionale',
    registerOrganizerTitle: 'Registrazione Organizzatore',
    registerSingerTitle: 'Registrazione Cantante',
    registerOrganizerDescription: 'Crea il tuo account per organizzare eventi karaoke',
    registerSingerDescription: 'Crea il tuo account per partecipare agli eventi karaoke',
    setPassword: 'Crea password',
    setPasswordTitle: 'Completa la registrazione',
    setPasswordDescription: 'Crea la tua password per completare la registrazione',
    createPassword: 'Crea la mia password',
    passwordRequirements: 'Minimo 12 caratteri, con maiuscola, minuscola, numero e carattere speciale',
    forgotPasswordDescription: 'Inserisci il tuo indirizzo email e ti invieremo un link per reimpostare la tua password.',
    sendResetLink: 'Invia link di reimpostazione',
    resetPasswordEmailSent: 'Email di reimpostazione inviata',
    resetPasswordInstructions: 'È stata inviata un\'email di reimpostazione a {{email}}. Fai clic sul link nell\'email per reimpostare la tua password.',
    resetPassword: 'Reimposta password',
    resetPasswordDescription: 'Crea una nuova password per il tuo account',
    resetPasswordButton: 'Reimposta la mia password',
    passwordResetSuccess: 'Password reimpostata',
    passwordResetSuccessDescription: 'La tua password è stata reimpostata con successo. Sarai reindirizzato alla pagina di accesso.',
    resetLinkExpired: 'Collegamento di reimpostazione scaduto',
    resetLinkExpiredDescription: 'Questo collegamento di reimpostazione è scaduto (valido per 1 ora). Si prega di richiedere un nuovo collegamento.',
    requestNewResetLink: 'Richiedi un nuovo collegamento',
    rememberPassword: 'Ricordi la tua password?',
  },
  // Organizer menu
  organizerMenu: {
    menu: 'Menu organizzatore',
    dashboard: 'Cruscotto',
    venues: 'Luoghi',
    settings: 'Impostazioni',
    dataPrivacy: 'Dati Personali',
    subscriptions: 'Prezzi',
    about: 'Chi siamo',
    user: 'Utente',
  },
  errors: {
    genericMessage: 'Si è verificato un errore',
    required: 'Questo campo è obbligatorio',
    invalidEmail: 'Email non valida',
    passwordTooShort: 'La password deve contenere almeno 12 caratteri',
    passwordRequirements: 'La password deve contenere almeno una maiuscola, una minuscola, una cifra e un carattere speciale',
    passwordsDoNotMatch: 'Le password non corrispondono',
    loginFailed: 'Accesso fallito',
    registerFailed: 'Registrazione fallita',
    resetPasswordFailed: 'Reimpostazione password fallita',
    invalidToken: 'Token non valido o scaduto',
    networkError: 'Errore di rete',
    unauthorized: 'Non autorizzato',
    forbidden: 'Accesso negato',
    notFound: {
      title: 'Pagina non trovata',
      description: 'La pagina che stai cercando non esiste o è stata spostata.',
      explore: 'Esplora l\'applicazione',
    },
    server: {
      title: 'Errore del server',
      description: 'Si è verificato un errore imprevisto. Riprova più tardi.',
    },
    generic: {
      title: 'Ops, qualcosa è andato storto',
      description: 'Si è verificato un errore imprevisto. Riprova.',
    },
    serverError: 'Errore del server',
    // Error codes from backend
    VALIDATION_ERROR: 'Errore di validazione',
    CREATE_EVENT_ERROR: 'Errore durante la creazione dell\'evento',
    UPDATE_EVENT_ERROR: 'Errore durante l\'aggiornamento dell\'evento',
    EVENT_NOT_FOUND: 'Evento non trovato',
    VENUE_NOT_FOUND: 'Luogo non trovato',
    QUOTA_EXCEEDED_CREATE: 'Quota mensile raggiunta per la creazione di eventi',
    QUOTA_EXCEEDED_DATE_CHANGE: 'Quota del piano {{planName}} raggiunta per questo mese: Massimo {{limit}} evento(i) al mese. La tua quota si reimposterà alla fine del periodo di fatturazione.',
    SUBSCRIPTION_EVENT_CANNOT_CONVERT: 'Gli eventi inclusi nel tuo piano di abbonamento non possono essere convertiti in evento singolo',
    EVENT_DATE_NOT_REACHED: 'Impossibile attivare l\'evento prima della data programmata',
    EVENT_DATE_PAST: 'La data dell\'evento non può essere nel passato',
    EVENT_DATE_AFTER_EXPIRATION: 'La data dell\'evento non può essere successiva alla data di scadenza del tuo evento unico',
    // Validation errors by field and Zod code
    validation: {
      name: {
        too_small: 'Il nome dell\'evento è obbligatorio',
        required: 'Il nome dell\'evento è obbligatorio',
        invalid_type: 'Il nome dell\'evento è invalido',
      },
      date: {
        required: 'La data è obbligatoria',
        invalid_type: 'La data è invalida',
        invalid_date: 'La data è invalida',
        invalid_string: 'Il formato della data è invalido',
      },
      description: {
        invalid_type: 'La descrizione è invalida',
      },
      venueId: {
        invalid_type: 'Il luogo è invalido',
      },
    },
  },
  // Landing page
  landing: {
    languageLabel: 'Scegli la tua lingua',
    heroTitle: 'Le tue serate karaoke, semplificate.',
    heroSubtitle: 'Crea il tuo evento in 2 minuti. I tuoi ospiti scansionano un QR code, aggiungono le loro canzoni — tu tieni il controllo della serata.',
    ctaPrimary: 'Crea il mio primo karaoke — gratuito',
    ctaOrganize: 'Voglio organizzare',
    ctaOrganizeDesc: 'Crea eventi, gestisci la playlist, segui le esibizioni.',
    ctaSing: 'Voglio cantare',
    ctaSingDesc: 'Unisciti a un evento tramite QR code e aggiungi le tue canzoni.',
    alreadyAccount: 'Hai già un account?',
    login: 'Accedi',
    whyTitle: 'Perché KaraoNow?',
    benefit1Title: 'Un QR code, zero carta',
    benefit1Desc: 'I tuoi ospiti scansionano e scelgono le canzoni dal telefono. Niente più liste perse.',
    benefit2Title: 'Playlist in diretta',
    benefit2Desc: 'Ordine di esecuzione visibile a tutti. Procedi senza intoppi.',
    benefit3Title: 'Gratuito per iniziare',
    benefit3Desc: 'Fino a 4 eventi gratuiti. Passa a illimitato quando vuoi.',
    twoWaysTitle: 'Due modi di vivere KaraoNow',
    learnMore: 'Scopri di più',
  },
  home: {
    title: 'Gestione playlist per eventi Karaoke',
    wantToSing: 'Voglio cantare',
    wantToSingDescription: 'Unisciti agli eventi e aggiungi le tue canzoni',
    wantToOrganize: 'Voglio organizzare',
    wantToOrganizeDescription: 'Crea e gestisci i tuoi eventi karaoke',
    alreadyHaveAccount: 'Hai già un account?',
  },
  dashboard: {
    title: 'Dashboard Organizzatore',
    welcome: 'Benvenuto',
    events: 'Eventi',
    createEvent: 'Crea evento',
    noEvents: 'Nessun evento',
    noEventsDescription: 'Inizia creando il tuo primo evento karaoke',
    filterAll: 'Tutti',
    filterDraft: 'Bozze',
    filterActive: 'Attivi',
    filterCompleted: 'Completati',
    filterCancelled: 'Annullati',
    loadingEvents: 'Caricamento eventi...',
    errorLoadingEvents: 'Errore nel caricamento degli eventi',
    noEventsMatchingFilter: 'Nessun evento corrisponde a questo filtro',
    tryDifferentFilter: 'Prova a selezionare un filtro diverso',
  },
  events: {
    name: 'Evento',
    events: 'Eventi',
    song: 'Canzone',
    songs: 'Canzoni',
    description: 'Descrizione',
    date: 'Data',
    time: 'Ora',
    venue: 'Luogo',
    selectVenue: 'Seleziona un luogo',
    namePlaceholder: 'La mia serata karaoke',
    descriptionPlaceholder: 'Descrizione opzionale...',
    createTitle: 'Crea Evento',
    createSubtitle: 'Crea un nuovo evento karaoke',
    createButton: 'Crea Evento',
    createEvent: 'Crea Evento',
    createSuccess: 'Evento creato con successo',
    createError: 'Errore durante la creazione dell\'evento',
    noVenues: 'Nessun luogo disponibile. Si prega di creare un luogo prima.',
    createVenue: 'Crea Luogo',
    status: 'Stato',
    statusDraft: 'Bozza',
    statusActive: 'Attivo',
    statusCompleted: 'Completato',
    statusCancelled: 'Annullato',
    statusHidden: 'Nascosto',
    createdAt: 'Creato il',
    updatedAt: 'Aggiornato il',
    view: 'Visualizza',
    open: 'Aprire',
    edit: 'Modifica',
    delete: 'Elimina',
    qrCode: 'Codice QR',
    deleteConfirmTitle: 'Elimina Evento',
    deleteConfirm: 'Sei sicuro di voler eliminare l\'evento "{{name}}" ?',
    deleteSuccess: 'Evento eliminato con successo',
    deleteError: 'Errore durante l\'eliminazione dell\'evento',
    managementTitle: 'Gestione Evento',
    editEvent: 'Modifica Evento',
    updateSuccess: 'Evento aggiornato con successo',
    updateError: 'Errore durante l\'aggiornamento dell\'evento',
    showQRCode: 'Mostra Codice QR',
    hideQRCode: 'Nascondi Codice QR',
    qrCodeTitle: 'Codice QR dell\'Evento',
    qrCodeDescription: 'Condividi questo codice con i tuoi partecipanti per permettere loro di unirsi all\'evento',
    copyQRCode: 'Copia Link',
    qrCodeCopied: 'Link copiato negli appunti',
    shareQRCode: 'Condividi',
    queue: 'Coda',
    singers: 'Cantanti',
    noQueue: 'Nessuna canzone nella coda',
    noSingers: 'Nessun cantante registrato',
    guest: 'Ospite',
    singer: 'Cantante',
    songsCount_one: '{{count}} canzone',
    songsCount_other: '{{count}} canzoni',
    deleteQueueItem: 'Elimina canzone',
    deleteQueueItemConfirm: 'Sei sicuro di voler eliminare questa canzone dalla coda?',
    deleteSinger: 'Rimuovi cantante',
    deleteSingerConfirm: 'Sei sicuro di voler rimuovere questo cantante? Tutte le sue canzoni in coda verranno anche rimosse.',
    singerDeleteError: 'Errore durante la rimozione del cantante',
    moveUp: 'Sposta su',
    moveDown: 'Sposta giù',
    dragToReorder: 'Trascina per riordinare',
    setCurrent: 'Imposta come attuale',
    markCompleted: 'Segna come completato',
    cancel: 'Annulla',
    queueUpdateError: 'Errore durante l\'aggiornamento della coda',
    queueDeleteError: 'Errore durante l\'eliminazione della canzone',
    queueStatusPending: 'In attesa',
    queueStatusCurrent: 'Attuale',
    queueStatusCompleted: 'Completato',
    queueStatusCancelled: 'Annullato',
    qrCodeUrl: 'URL dell\'evento',
    qrCodeShareText: 'Unisciti a questo evento karaoke: {{eventName}}!',
    qrCodePrintTitle: 'Codice QR - KaraoNow',
    qrCodePrintInstructions: 'Scansiona questo codice QR per accedere all\'evento e aggiungere le tue canzoni alla coda.',
    qrCodePrint: 'Stampa',
    qrCodeShare: 'Condividi',
    qrCodeDownload: 'Scarica',
    startEvent: 'Avvia l\'evento',
    start: 'Avvia',
    completeEvent: 'Completa l\'evento',
    complete: 'Completa',
    cancelEvent: 'Annulla l\'evento',
    cancelConfirmTitle: 'Conferma annullamento',
    cancelConfirmMessage: 'Sei sicuro di voler annullare questo evento? Questa azione è definitiva.',
    cancelConfirmOneShotTitle: 'Conferma annullamento - Evento unico',
    cancelConfirmOneShotMessage: '⚠️ Attenzione: Questo evento unico è stato acquistato. L\'annullamento è definitivo e non verrà effettuato alcun rimborso se l\'evento non è stato utilizzato. Sei sicuro di voler annullare questo evento?',
    cancelConfirmButton: 'Sì, annulla',
    cancelCancelButton: 'No, mantieni l\'evento',
    deleteEvent: 'Elimina l\'evento',
    deleteEventConfirm: 'Sei sicuro di voler eliminare questo evento? Questa azione è irreversibile.',
    hide: 'Nascondi',
    hideConfirmTitle: 'Nascondi evento',
    hideConfirm: 'Nascondere l\'evento "{{name}}" ? Non sarà più visibile nella tua lista principale, ma continuerà a contare nella tua quota mensile.',
    hideSuccess: 'Evento nascosto con successo',
    hideError: 'Errore durante il nascondimento dell\'evento',
    hidden: 'Nascosto',
    showHidden: 'Nascosti',
    hiddenEventTitle: 'Evento nascosto',
    hiddenEventMessage: 'Questo evento è stato nascosto e non è più accessibile. Continua a contare nella tua quota mensile.',
    hiddenEventTooltip: 'Questo evento è nascosto e non può essere visualizzato',
    statusUpdated: 'Stato dell\'evento aggiornato',
    backToEvent: 'Torna all\'evento',
    // Quota exceeded for date change
    quotaExceededDateChange: 'Quota mensile raggiunta per questa data',
    quotaExceededDateChangeTitle: 'Quota mensile raggiunta',
    quotaExceededDateChangeExplanation: 'Non è possibile modificare la data dell\'evento "{{eventName}}" poiché supererebbe la quota mensile di eventi.',
    quotaExceededDateChangeOptions: 'Puoi mantenere la data originale o convertire questo evento in "Evento Singolo" per rimuoverlo dalla quota mensile.',
    quotaExceededDetails: 'Hai {{current}} evento(i) su {{limit}} per questo periodo.',
    upgradeToOneShotOption: 'Opzione: Passa a Evento Singolo',
    upgradeToOneShotDescription: 'Convertendo questo evento in "Evento Singolo", non conterà più nella quota mensile e potrai modificare la data liberamente.',
    upgradeToOneShotNow: '🚀 Passa a Evento Singolo',
    keepOriginalDate: 'Mantieni la data originale',
    multipleEventsPremiumSuggestion: 'Più eventi? Il piano Premium potrebbe essere più conveniente.',
    // Quota exceeded for event creation
    quotaExceededCreateTitle: 'Quota mensile raggiunta',
    quotaExceededCreateMessage: 'Hai raggiunto il limite del tuo {{planName}}. Non puoi creare più eventi per questo periodo.',
    quotaExceededCreateExplanation: 'La tua quota mensile di eventi è raggiunta. Puoi attendere la fine del periodo di fatturazione o passare a un piano superiore.',
    quotaExceededCreateOptions: 'Puoi anche creare un evento singolo che non conterà nella tua quota mensile.',
    // Quota exceeded for FREE plan (no monthly/billing period references)
    quotaExceededCreateTitleFree: 'Quota raggiunta',
    quotaExceededCreateMessageFree: 'Hai raggiunto il limite del tuo {{planName}}. Non puoi creare più eventi.',
    quotaExceededCreateExplanationFree: 'Hai raggiunto il limite di eventi del tuo piano gratuito. Puoi passare a un piano superiore per creare più eventi.',
    quotaExceededCreateOptionsFree: 'Puoi anche creare un evento singolo che non conterà nella tua quota.',
    quotaExceededDetailsFree: 'Hai {{current}} evento(i) su {{limit}}.',
    createOneShotDescriptionFree: 'Crea un evento singolo che non conterà nella tua quota.',
    upgradePlanDescriptionFree: 'Passa a un piano superiore per aumentare la tua quota di eventi.',
    createOneShotOption: 'Opzione: Crea un Evento Singolo',
    createOneShotDescription: 'Crea un evento singolo che non conterà nella tua quota mensile.',
    createOneShotNow: '🚀 Crea un Evento Singolo',
    upgradePlanOption: 'Opzione: Passa al piano superiore',
    upgradePlanDescription: 'Passa a un piano superiore per aumentare la tua quota mensile di eventi.',
    upgradePlanNow: 'Passa al piano superiore',
    quotaResetInfo: 'La tua quota verrà reimpostata alla fine del periodo di fatturazione.',
    // Generic quota exceeded message
    quotaExceeded: 'Quota mensile raggiunta per questa data',
    // Plan subscription badge
    planSubscription: 'Incluso nel piano',
    planSubscriptionTooltip: 'Evento incluso nel tuo abbonamento',
  },
  // Venues
  venues: {
    title: 'Gestione Luoghi',
    subtitle: 'Crea e gestisci i tuoi luoghi per karaoke',
    venuesList: 'Elenco Luoghi',
    addVenue: 'Aggiungi Luogo',
    editVenue: 'Modifica Luogo',
    noVenues: 'Nessun luogo registrato',
    noVenuesDescription: 'Inizia creando il tuo primo luogo',
    eventName: 'Nome dell\'evento',
    eventNamePlaceholder: 'Karaoke di {{firstName}}',
    name: 'Nome del Luogo',
    namePlaceholder: 'Es: Bar Le Chat Noir',
    address: 'Indirizzo',
    addressPlaceholder: 'Es: Via della Musica 123',
    city: 'Città',
    cityPlaceholder: 'Es: Roma',
    country: 'Paese',
    countryPlaceholder: 'Es: Italia',
    googleMapsUrl: 'Link Google Maps',
    googleMapsUrlPlaceholder: 'https://maps.google.com/...',
    viewOnGoogleMaps: 'Vedi su Google Maps',
    addSuccess: 'Luogo creato con successo',
    addError: 'Errore durante la creazione del luogo',
    updateSuccess: 'Luogo aggiornato con successo',
    updateError: 'Errore durante l\'aggiornamento del luogo',
    deleteSuccess: 'Luogo eliminato con successo',
    deleteError: 'Errore durante l\'eliminazione del luogo',
    confirmDelete: 'Sei sicuro di voler eliminare il luogo "{{name}}" ?',
    confirmDeleteTitle: 'Elimina Luogo',
    cancel: 'Annulla',
    add: 'Aggiungi',
    edit: 'Modifica',
    save: 'Salva',
    deleting: 'Eliminazione...',
    saving: 'Salvataggio...',
    generateMapsUrl: 'Genera link Google Maps',
    generateMapsUrlError: 'Compila almeno il nome del luogo o la città per generare il link',
    mapsModal: {
      title: 'Seleziona posizione su Google Maps',
      description: 'Usa Google Maps per trovare e regolare con precisione la posizione del tuo luogo.',
      instructions: 'Clicca "Apri in Google Maps" qui sotto, regola il punto sulla mappa se necessario, poi copia il link dalla barra degli indirizzi del browser e incollalo nel campo qui sotto.',
      urlLabel: 'Link Google Maps',
      openInMaps: 'Apri in Google Maps',
      confirm: 'Conferma',
      clickToOpen: 'Clicca per aprire Google Maps',
      clickDescription: 'Si aprirà una nuova finestra con Google Maps in modo da poter regolare la posizione',
    },
  },
  // Singer interface
  singer: {
    // Dashboard
    dashboard: 'Pannello di controllo',
    dashboardSubtitle: 'Visualizza i tuoi eventi e le tue canzoni',
    myEvents: 'I miei eventi',
    mySongs: 'Le mie canzoni',
    allEvents: 'Tutti',
    activeEvents: 'Attivi',
    pastEvents: 'Passati',
    inactiveEvents: 'Inattivi',
    sortByDate: 'Ordina per data',
    sortByOrganizer: 'Ordina per organizzatore',
    sortBySongs: 'Ordina per numero di canzoni',
    noEvents: 'Non hai ancora partecipato a nessun evento.',
    noSongs: 'Non hai ancora aggiunto canzoni.',
    eventName: 'Nome evento',
    organizer: 'Organizzatore',
    date: 'Data',
    songsAdded: 'Canzoni aggiunte',
    songsSung: 'Canzoni cantate',
    status: 'Stato',
    status_active: 'Attivo',
    status_completed: 'Completato',
    status_cancelled: 'Annullato',
    status_draft: 'Bozza',
    title: 'Titolo',
    artist: 'Artista',
    event: 'Evento',
    addedDate: 'Data di aggiunta',
    totalEvents: 'Evento totali',
    totalSongs: 'Canzoni totali',
    uniqueOrganizers: 'Organizzatori unici',
    // Settings
    settings: 'Impostazioni',
    settingsSubtitle: 'Gestisci il tuo profilo e i tuoi dati',
    menu: 'Menu del cantante',
    profile: 'Profilo',
    changePassword: 'Cambia password',
    changePasswordDescription: 'Ti invieremo un link per reimpostare la tua password via email.',
    sendResetLink: 'Invia link di reimpostazione',
    passwordResetLinkSent: 'Un link di reimpostazione della password è stato inviato al tuo indirizzo email.',
    accountData: 'Dati dell\'account',
    editProfile: 'Modifica il mio profilo',
    saveChanges: 'Salva le modifiche',
    profileUpdated: 'Profilo aggiornato con successo',
    changePasswordTitle: 'Cambia la mia password',
    currentPassword: 'Password attuale',
    newPassword: 'Nuova password',
    confirmNewPassword: 'Conferma nuova password',
    passwordRequirements: 'La password deve contenere almeno 12 caratteri, una maiuscola, una minuscola, un numero e un carattere speciale.',
    passwordChanged: 'Password modificata con successo',
    passwordTooShort: 'La password deve contenere almeno 12 caratteri',
    passwordsNotMatch: 'Le password non corrispondono',
    currentPasswordRequired: 'La password attuale è obbligatoria',
    // Errori di convalida della password
    passwordMinLength: 'La password deve contenere almeno 12 caratteri',
    passwordNoUppercase: 'La password deve contenere almeno una lettera maiuscola',
    passwordNoLowercase: 'La password deve contenere almeno una lettera minuscola',
    passwordNoDigit: 'La password deve contenere almeno un numero',
    passwordNoSpecialChar: 'La password deve contenere almeno un carattere speciale',
    exportData: 'Esporta i miei dati',
    exportDataDescription: 'Scarica una copia di tutti i tuoi dati personali in formato JSON.',
    downloadData: 'Scarica i miei dati',
    dataExported: 'Dati esportati con successo',
    dangerZone: 'Zona pericolosa',
    deleteAccountDescription: 'L\'eliminazione del tuo account è irreversibile. Tutti i tuoi dati verranno eliminati permanentemente.',
    deleteAccount: 'Elimina il mio account',
    deleteAccountConfirm: 'Elimina',
    confirmDeleteAccount: 'Sei sicuro di voler eliminare il tuo account? Questa azione è irreversibile.',
    // QR Code Landing
    qrCodeMissing: 'Token codice QR mancante',
    qrCodeError: 'Errore durante la validazione del codice QR',
    eventNotFound: 'Evento non trovato o token non valido',
    organizerNotAllowed: 'Sei un organizzatore. Usa la tua dashboard per gestire questo evento.',
    accessDenied: 'Accesso non autorizzato',
    validatingQRCode: 'Validazione codice QR...',
    chooseAccessMethod: 'Scegli come partecipare all\'evento',
    continueAsGuest: 'Continua come ospite',
    orWithAccount: 'o con un account',
    enterPseudo: 'Inserisci il tuo pseudonimo per partecipare all\'evento',
    joinEvent: 'Partecipa all\'evento',
    joining: 'Connessione...',
    sessionError: 'Errore durante la creazione della sessione',
    joinError: 'Errore durante la partecipazione all\'evento',
    sessionExpired: 'La tua sessione è scaduta. Per favore, scansiona nuovamente il codice QR dell\'evento.',
    guestMode: 'Modalità ospite',
    leaveEvent: 'Lascia l\'evento',
    // Singer Event Page
    eventNotActive: 'Questo evento non è ancora attivo. Torna più tardi!',
    eventNotStarted: 'Questo evento non è ancora iniziato. Sarà disponibile una volta che l\'organizzatore avvierà il karaoke!',
    eventNotYetAvailable: 'Questo evento non è ancora disponibile. Si prega di attendere fino alla data programmata.',
    eventEnded: 'Questo evento è terminato. I voti non sono più accettati.',
    eventCancelled: 'Questo evento è stato annullato. I voti non sono disponibili.',
    searchSong: 'Cerca una canzone',
    searchResults: 'Risultati della ricerca',
    advancedSearch: 'Ricerca avanzata',
    advancedSearchToggle: 'Ricerca avanzata',
    simpleSearch: 'Ricerca semplice',
    searchPlaceholder: 'Titolo o artista...',
    titlePlaceholder: 'Titolo...',
    artistPlaceholder: 'Artista...',
    noResults: 'Nessun risultato trovato',
    addCustomSong: 'Aggiungi questo titolo',
    addCustomSongDescription: 'Questo titolo verrà salvato nel database e aggiunto alla tua coda.',
    addAndQueue: 'Aggiungi e metti in coda',
    localResults: '{{count}} risultato(i) trovato(i) nel nostro database',
    musicbrainzResults: '{{count}} risultato(i) MusicBrainz',
    moreResults: 'Altri risultati',
    addError: 'Errore durante l\'aggiunta alla coda',
    queueLimitReached: 'Limite raggiunto: l\'organizzatore di questo evento sta utilizzando il piano gratuito che è limitato a {{limit}} canzone/i massimo nella coda. La coda contiene attualmente {{currentCount}} canzone/i. Si prega di contattare l\'organizzatore se si desidera aggiungere più canzoni.',
    songAdded: '"{{title}}" aggiunto alla coda!',
    myQueue: 'Le mie canzoni',
    noSongsInQueue: 'Non hai ancora aggiunto canzoni',
    fullQueue: 'Coda',
    queueEmpty: 'La coda è vuota',
    you: 'Tu',
    unknown: 'Sconosciuto',
    // Queue statuses
    statusPending: 'In attesa',
    statusCurrent: 'In corso',
    statusCompleted: 'Completato',
    statusCancelled: 'Annullato',
  },
  // Singer menu
  singerMenu: {
    about: 'Chi siamo',
    dataPrivacy: 'Dati Personali',
  },
  // Subscription and Plans
  subscription: {
    title: 'Piani e prezzi',
    subtitle: 'Scegli il piano che si adatta alle tue esigenze',
    currentPlan: 'Piano attuale',
    planFree: 'Piano Gratuito',
    planOneShot: 'Evento Singolo',
    planPro: 'Piano Pro',
    planPremium: 'Piano Premium',
    planUltimate: 'Piano Ultimate',
    planFreeDescription: 'Perfetto per iniziare',
    planOneShotDescription: 'Per un evento',
    planProDescription: 'Per organizzatori regolari',
    planPremiumDescription: 'Per gli appassionati',
    planUltimateDescription: 'Per professionisti',
    priceFreeFree: 'Gratuito',
    priceOneShotPer: '{{price}}€ / evento',
    priceProPer: '{{price}}€ / mese',
    pricePremiumPer: '{{price}}€ / mese',
    priceUltimatePer: '{{price}}€ / mese',
    maxEvents: 'Eventi massimi',
    maxSongsPerEvent: 'Canzoni per evento',
    unlimited: 'Illimitato',
    features: 'Caratteristiche',
    currentPlanBadge: 'Piano attuale',
    changePlan: 'Cambia piano',
    upgradePlan: 'Passa a premium',
    upgradePlanButton: 'Passa al piano superiore',
    selectPlan: 'Seleziona questo piano',
    monthlyQuota: 'Quota mensile',
    freeQuota: 'Quota gratuita',
    eventsUsed: '{{current}} / {{limit}} eventi',
    billingPeriod: 'Periodo: {{start}} - {{end}}',
    quotaReached: 'Quota mensile raggiunta',
    quotaReachedMessage: 'Hai raggiunto il limite di eventi per questo periodo. La quota verrà reimpostata alla fine del periodo di fatturazione.',
    quotaWarning: 'Quota mensile quasi raggiunta',
    quotaWarningMessage: 'Ti rimangono {{remaining}} evento(i) per questo periodo.',
    quotaInfo: 'Ti rimangono {{remaining}} evento(i) per questo periodo.',
    learnMore: 'Ulteriori informazioni',
    contactSupport: 'Contatta il supporto',
    freePlanNote: 'Il piano gratuito è limitato a {{maxEvents}} eventi (tutti gli stati, escluso Evento Singolo) e {{maxSongsPerEvent}} canzoni per evento.',
    oneShotPlanNote: 'Numero illimitato di canzoni nella playlist dell\'evento. Valido per 15 giorni.',
    proPlanNote: '{{maxEvents}} eventi al mese (periodo mobile). Canzoni illimitate.',
    premiumPlanNote: '{{maxEvents}} eventi al mese (periodo mobile). Canzoni illimitate.',
    ultimatePlanNote: 'Eventi e canzoni illimitati.',
    noEngagement: 'Senza impegno.',
    convertEvent: 'Converti un evento',
    createEvent: 'Crea un evento',
    chooseOption: 'Scegli un\'opzione:',
    convertDescription: 'Seleziona un evento gratuito e convertilo in un piano Evento Singolo.',
    createDescription: 'Crea un nuovo evento con il piano Evento Singolo.',
    noFreeEventsToConvert: 'Non hai un evento GRATUITO disponibile da convertire.',
    selectFreeEvent: 'Seleziona un evento GRATUITO da convertire:',
    convertThisEvent: 'Converti questo evento',
    createNewOneShot: 'Crea un evento Evento Singolo',
    newOneShotEventName: 'Nuovo evento singolo',
    createEventError: 'Errore durante la creazione dell\'evento',
    createOneShotEventTitle: 'Crea un evento singolo',
    proceedToPayment: 'Procedi al pagamento',
    // Stripe checkout
    checkoutError: 'Si è verificato un errore durante il pagamento',
    paymentSuccessful: 'Pagamento riuscito!',
    paymentProcessing: 'Il tuo pagamento è in fase di elaborazione. Sarai reindirizzato a breve.',
    redirectingIn5: 'Reindirizzamento automatico in 5 secondi...',
    backToSubscription: 'Torna ai piani',
    successTitle: 'Pagamento confermato',
    successSubtitle: 'Grazie per il tuo acquisto',
    paymentCancelled: 'Pagamento annullato',
    checkoutCancelledMessage: 'Hai annullato il pagamento. Puoi riprovare in qualsiasi momento.',
    cancelledTitle: 'Pagamento annullato',
    cancelledSubtitle: 'Torna ai piani per riprovare',
    whatHappensNext: 'Cosa succede dopo?',
    webhooksProcessing: 'Stiamo elaborando il tuo pagamento',
    subscriptionActive: 'Il tuo abbonamento sarà attivo presto',
    confirmationEmail: 'Riceverai un\'email di conferma',
    // Subscription cancellation
    cancelConfirm: 'Sei sicuro di voler annullare il tuo abbonamento?',
    cancelDetails: 'Manterrai l\'accesso al tuo piano Premium fino alla fine del tuo attuale periodo di fatturazione. Dopo quella data, verrai declassato al piano Gratuito.',
    cancelSuccess: 'Il tuo abbonamento è stato annullato con successo.',
    cancelPlan: 'Annulla',
    cancellationInProgress: 'L\'annullamento del tuo abbonamento è già in corso. Passerai al piano Gratuito alla fine del tuo periodo di fatturazione.',
    cancellationInProgressTitle: 'Annullamento in Corso',
    cancellationInProgressMessage: 'L\'annullamento del tuo abbonamento è già in corso. Passerai al piano Gratuito alla fine del tuo periodo di fatturazione attuale. Non è richiesta alcuna azione aggiuntiva.',
    accessUntil: 'Avrai accesso fino al {{date}}.',
    cancelError: 'Si è verificato un errore durante l\'annullamento del tuo abbonamento',
    cancelModalTitle: 'Annulla Abbonamento',
    cancelModalMessage: 'Conserverai l\'accesso al tuo piano Premium fino alla fine del tuo attuale periodo di fatturazione. Dopo quella data, passerai automaticamente al piano Gratuito.',
    cancelModalConfirm: 'Sì, annulla il mio abbonamento',
    endOfBillingPeriod: 'fine del periodo di fatturazione',
    // ONE_SHOT upgrade
    upgradeToOneShot: 'Aggiorna a Evento Singolo',
    upgradeOneShotConfirm: 'Stai per aggiornare l\'evento "{{eventName}}" al piano Evento Singolo.',
    oneShotDescription: 'Questo è un pagamento unico per canzoni illimitate in questo evento specifico.',
    price: 'Prezzo',
    oneTimePayment: 'Pagamento unico',
    whatYouGet: 'Cosa otterrai:',
    unlimitedSongs: 'Canzoni illimitate per questo evento',
    noLimits: 'Nessun limite di coda',
    oneShotValidity: 'Valido fino al completamento dell\'evento',
    upgradeNow: 'Aggiorna ora',
    upgradeError: 'Errore nell\'aggiornamento dell\'evento',
    updateSuccess: 'Abbonamento aggiornato con successo al piano {{plan}}',
    changePlanTitle: 'Cambia piano',
    changePlanSubtitle: 'Stai per modificare il tuo abbonamento',
    newPlan: 'Nuovo piano',
    perMonth: 'mese',
    upgradeCost: 'Costo aggiuntivo: +{{amount}}€ / mese',
    downgradeCredit: 'Credito stimato: -{{amount}}€ / mese',
    immediateChange: 'Cambio immediato',
    immediateChangeDescription: 'Il cambio di piano sarà effettivo immediatamente. La fatturazione prorata sarà calcolata automaticamente da Stripe.',
    confirmChange: 'Conferma cambio',
    oneShot: 'Evento singolo',
    oneShotTooltip: 'Sottoscrizione evento unico',
    expiresLabel: 'Scade il',
    oneShotExpirationTitle: 'Scadenza del tuo evento unico',
    oneShotDaysRemaining: '{{count}} giorno rimanente',
    oneShotDaysRemaining_plural: '{{count}} giorni rimanenti',
    oneShotExpiresOn: 'Scade il {{date}}',
    oneShotExpirationWarning: '⚠️ Il tuo evento tornerà al piano gratuito dopo questa data. Assicurati di usarlo prima della scadenza!',
    oneShotValidityInfo: 'Questo acquisto è valido per {{days}} giorni dalla data di acquisto. Usalo prima della scadenza!',
    upgradeToUnlimited: '🚀 Passa a illimitato',
    upgradeToUnlimitedNow: '🚀 Passa a illimitato ora',
    upgradeSingleEventConfirm: 'Stai per passare l\'evento "{{eventName}}" a illimitato.',
    singleEventDescription: 'Si tratta di un pagamento unico per canzoni illimitate in questo evento specifico.',
    singleEventPlan: 'Evento singolo',
    multiplePremiumSuggestion: 'Più eventi? Il piano Premium potrebbe essere più vantaggioso.',
    comparePlans: 'Confronta i piani →',
    premium: 'PREMIUM',
  },
  // Plan alerts
  planAlert: {
    limitReached: 'Limite del piano raggiunto',
    songsLimitReached: 'Hai raggiunto il limite del piano gratuito ({{limit}} canzone/i). Per aggiungere più canzoni, sottoscrivi un piano a pagamento.',
    eventsLimitReached: 'Hai raggiunto il limite del piano gratuito ({{limit}} evento/i). Per creare più eventi, sottoscrivi un piano a pagamento.',
    upgrade: 'Sottoscrivi',
    dismissible: true,
  },
  pwa: {
    install: 'Installa',
    installApp: 'Installa l\'app',
    installPrompt: 'Installa KaraoNow per un accesso rapido!',
    installed: 'App installata',
    offline: 'Sei offline',
    offlineMessage: 'Alcune funzionalità potrebbero essere limitate.',
    updateAvailable: 'Aggiornamento disponibile',
    updateNow: 'Aggiorna ora',
  },
  // About
  about: {
    title: 'Chi siamo - KaraoNow',
    subtitle: 'Scopri l\'app che trasforma i tuoi eventi karaoke',
    introduction: 'Cos\'è KaraoNow?',
    introductionText: 'KaraoNow è una piattaforma SaaS innovativa progettata per gestire i tuoi eventi karaoke in modo fluido e professionale. Che tu sia un organizzatore di eventi o un cantante appassionato, KaraoNow ti fornisce gli strumenti necessari per creare un\'esperienza karaoke indimenticabile.',
    missionText: 'La nostra missione è semplificare la gestione della playlist karaoke e creare una connessione autentica tra organizzatori e cantanti, rendendo ogni evento memorabile e ben organizzato.',
    features: 'Caratteristiche principali',
    feature1Title: 'Gestione degli eventi',
    feature1Desc: 'Crea e gestisci facilmente i tuoi eventi karaoke con tutti i dettagli necessari.',
    feature2Title: 'Partecipazione interattiva',
    feature2Desc: 'Consenti ai cantanti di unirsi ai tuoi eventi tramite codice QR e votare le loro canzoni preferite.',
    feature3Title: 'Playlist in tempo reale',
    feature3Desc: 'Visualizza e gestisci la coda delle canzoni in diretta con aggiornamenti istantanei.',
    feature4Title: 'Statistiche dettagliate',
    feature4Desc: 'Analizza le prestazioni dei tuoi eventi con grafici e metriche in tempo reale.',
    feature5Title: 'Sicurezza migliorata',
    feature5Desc: 'I tuoi dati sono protetti da elevati standard di sicurezza e infrastrutture cloud robuste.',
    feature6Title: 'Supporto multilingue',
    feature6Desc: 'KaraoNow supporta 8 lingue per raggiungere un pubblico globale.',
    technology: 'Stack tecnologico',
    frontend: 'Frontend',
    backend: 'Backend',
    version: 'Versione attuale',
    versionNote: 'Aggiornata regolarmente con nuove funzionalità',
    visitWebsite: 'Sito ufficiale',
    support: 'Supporto',
    supportNote: 'Non esitare a contattarci per qualsiasi domanda',
    copyright: 'Tutti i diritti riservati.',
    privacyNotice: 'Consulta le nostre pagine legali per saperne di più sulla protezione dei dati.',
    tryForFree: 'Inizia la tua prova gratuita',
  },
  // Organizer
  organizer: {
    // Dashboard
    dashboard: 'Pannello di controllo',
    dashboardSubtitle: 'Gestisci i tuoi eventi karaoke',
    menu: 'Menu organizzatore',
    // Settings
    settings: 'Impostazioni',
    settingsSubtitle: 'Gestisci il tuo profilo e le tue impostazioni',
    editProfile: 'Modifica il mio profilo',
    saveChanges: 'Salva modifiche',
    profileUpdated: 'Profilo aggiornato con successo',
    emailChangeConfirmTitle: 'Modifica dell\'indirizzo email',
    emailChangeConfirmMessage: 'Verrà inviata un\'email di conferma al tuo nuovo indirizzo email. La tua nuova email sarà effettiva solo dopo aver convalidato il link in quell\'email. La tua vecchia email rimane attiva fino alla convalida.',
    changePassword: 'Cambia password',
    changePasswordDescription: 'Ti invieremo un collegamento per reimpostare la tua password tramite email.',
    sendResetLink: 'Invia collegamento di reimpostazione',
    passwordResetLinkSent: 'Un collegamento per reimpostare la password è stato inviato alla tua email.',
    accountData: 'Dati dell\'account',
    exportData: 'Esporta i miei dati',
    exportDataDescription: 'Scarica una copia di tutti i tuoi dati personali in formato JSON.',
    downloadData: 'Scarica i miei dati',
    dataExported: 'Dati esportati con successo',
    dangerZone: 'Zona pericolosa',
    deleteAccountDescription: 'L\'eliminazione del tuo account è irreversibile. Tutti i tuoi dati verranno eliminati definitivamente.',
    deleteAccount: 'Elimina il mio account',
    deleteAccountConfirm: 'Elimina',
    confirmDeleteAccount: 'Sei sicuro di voler eliminare il tuo account? Questa azione è irreversibile.',
    // Plan section
    planSection: 'Il mio abbonamento',
    planSectionDescription: 'Visualizza il tuo piano attuale, i limiti e le opzioni di aggiornamento',
    renewDate: 'Rinnovo il ',
    viewPlanDetails: 'Visualizza dettagli del piano',
    eventsLimit: 'Eventi creati (escluso Evento Unico)',
    subscriptionStatus: 'Stato',
    statusActive: 'Attivo',
    statusInactive: 'Inattivo',
    statusScheduledEnd: 'Fine Programmata',
    subscriptionExpires: 'Scade il',
    subscriptionRenewsOn: 'Si rinnova il',
    cancellationConfirmed: 'Cancellazione Confermata!',
    continueAccessUntil: 'Continui a goderti il tuo piano Premium fino al {{date}}',
    autoDowngradeToFree: 'Passerai automaticamente al piano Gratuito dopo questa data.',
    accessUntilEndOfPeriod: 'Mantieni l\'accesso fino alla fine del tuo periodo di fatturazione attuale.',
    planFeatures: 'Caratteristiche del piano',
    featureEvents: 'Eventi al mese',
    featureSongsPerEvent: 'Canzoni per evento',
    unlimited: 'Illimitato',
    upgradePlan: 'Aggiorna il mio piano',
    managePlan: 'Gestisci il mio abbonamento',
  },
  // Email change
  email: {
    changeEmail: 'Cambia il mio indirizzo email',
    changeEmailDescription: 'Per cambiare il tuo indirizzo email, devi prima confermare il nuovo indirizzo. Un\'email di conferma sarà inviata al nuovo indirizzo.',
    currentEmail: 'Email attuale',
    newEmail: 'Nuovo indirizzo email',
    newEmailAddress: 'Nuovo indirizzo',
    requestEmailChange: 'Richiedi cambio email',
    emailRequired: 'L\'indirizzo email è obbligatorio',
    emailUnchanged: 'Il nuovo indirizzo email deve essere diverso da quello attuale',
    emailChangeConfirmationSent: 'Un\'email di conferma è stata inviata al tuo nuovo indirizzo email.',
    emailChangeInfo: 'La tua vecchia email rimane attiva finché il nuovo indirizzo non viene validato.',
    verifying: 'Verifica in corso',
    verifyingDescription: 'Verifica del tuo indirizzo email in corso...',
    success: 'Email confermata',
    confirmationSuccess: 'Il tuo indirizzo email è stato cambiato con successo!',
    confirmationFailed: 'Conferma fallita',
    linkExpired: 'Link scaduto',
    tokenExpired: 'Il link di conferma è scaduto. Si prega di richiederne uno nuovo.',
    linkExpiredDescription: 'Il link di conferma è scaduto (valido per 24 ore). Si prega di richiedere un nuovo link di conferma.',
    redirecting: 'Reindirizzamento in corso...',
    redirectingToLogin: 'Reindirizzamento al login in 3 secondi...',
    goToSettings: 'Vai alle impostazioni',
    backToSettings: 'Torna alle impostazioni',
    requestNewLink: 'Richiedi un nuovo link',
    missingToken: 'Token di conferma mancante.',
    validationPending: 'Validazione in sospeso',
    pendingEmailAddress: 'In attesa di validazione',
  },
  // RGPD - Privacy dei Dati
  rgpd: {
    pageTitle: 'Dati Personali',
    pageSubtitle: 'Gestisci i tuoi dati e il tuo account in modo sicuro',
    exportData: 'Scarica I Miei Dati',
    exportDataDescription: 'Scarica una copia completa dei tuoi dati personali in formato JSON. Puoi utilizzare questi dati per importarli altrove o semplicemente conservarli come backup.',
    exportIncludes: 'Questa esportazione include',
    exportProfile: 'Profilo e preferenze',
    exportHistory: 'Cronologia degli eventi',
    exportEvents: 'Eventi creati',
    exportSubscription: 'Informazioni di abbonamento',
    downloadData: 'Scarica (JSON)',
    exporting: 'Scaricamento in corso...',
    exportFormatJSON: 'Formato: JSON (conforme al GDPR)',
    dataExportedSuccess: '✅ I tuoi dati sono stati scaricati con successo!',
    deleteAccount: 'Elimina Il Mio Account',
    deleteAccountDescription: 'Questa azione eliminerà permanentemente il tuo account e tutti i dati associati. Questa operazione non può essere annullata.',
    deleteWarning: '⚠️ Avvertenza Importante',
    deleteIrreversible: 'Questa azione è permanente e irreversibile',
    deleteAllData: 'Tutti i tuoi dati verranno eliminati',
    deleteNoRecovery: 'Non ci sarà possibilità di recupero',
    deleteRecommendation: 'Ti consigliamo di scaricare i tuoi dati prima di eliminare il tuo account.',
    deleteAccountButton: 'Elimina Il Mio Account',
    confirmDeletion: 'Conferma eliminazione',
    deleteConfirmMessage: 'Sei sicuro di voler eliminare il tuo account? Questa azione non può essere annullata.',
    continueDelete: 'Continua all\'eliminazione',
    finalDeleteWarning: 'Conferma Finale - Nessun Ritorno',
    finalDeleteMessage: 'Questa è la tua ultima opportunità! Fare clic su "Elimina Permanentemente" eliminerà il tuo account in modo irreversibile.',
    permanentlyDelete: 'Elimina Permanentemente',
    gdprCompliance: 'Conformità GDPR',
    gdprInfo: 'Secondo le normative GDPR, hai il diritto di accedere, rettificare, eliminare e portare i tuoi dati personali. Questi strumenti ti permettono di esercitare questi diritti.',
  },
  // Cookies consent
  cookies: {
    title: '🍪 Utilizziamo i cookie',
    description: 'Utilizziamo i cookie per migliorare la tua esperienza. Consulta la nostra politica per ulteriori informazioni.',
    understood: 'Capito',
    learnMore: 'Scopri di più',
    footerNote: 'Puoi consultare la nostra Politica sui cookie per ulteriori dettagli.',
  },
  // Cookies policy page
  cookiesPage: {
    title: 'Politica sui Cookie',
    subtitle: 'Informazioni complete sui cookie utilizzati su KaraoNow',
    overview: 'Panoramica',
    overviewText: 'KaraoNow utilizza i cookie per migliorare la tua esperienza utente, proteggere il tuo account e capire come utilizzi la nostra piattaforma. Questa politica spiega come e perché utilizziamo i cookie.',
    whatAreCookies: 'Cosa sono i Cookie?',
    whatAreCookiesText: 'I cookie sono piccoli file di testo archiviati sul tuo dispositivo. Consentono ai siti web di ricordare informazioni sulle tue visite, come le tue preferenze e i dati di accesso.',
    typesOfCookies: 'Tipi di Cookie',
    sessionCookies: 'Cookie di Sessione',
    sessionCookiesText: 'Questi cookie scadono quando chiudi il tuo browser. Vengono utilizzati per mantenere attiva la tua sessione e proteggere il tuo accesso.',
    persistentCookies: 'Cookie Persistenti',
    persistentCookiesText: 'Questi cookie rimangono archiviati sul tuo dispositivo per un periodo definito. Ci permettono di riconoscerti alle tue prossime visite.',
    howWeUseCookies: 'Come Utilizziamo i Cookie',
    useAuthentication: 'Autenticazione e sicurezza della sessione',
    usePreferences: 'Memorizzazione delle tue preferenze (lingua, tema)',
    useSecurity: 'Protezione contro abusi e attacchi',
    usePerformance: 'Miglioramento delle prestazioni della piattaforma',
    yourRights: 'I Tuoi Diritti',
    yourRightsText: 'Hai il diritto di controllare i cookie. Puoi accettare o rifiutare cookie non essenziali tramite il nostro banner di consenso.',
    managingCookies: 'Gestire i Cookie',
    managingCookiesText: 'Puoi gestire i cookie tramite le impostazioni del tuo browser. Tieni presente che disabilitare alcuni cookie potrebbe influenzare il funzionamento di KaraoNow.',
    contact: 'Contatti',
    contactText: 'Per qualsiasi domanda su questa politica, contattaci tramite la nostra pagina di contatto o i termini di servizio.',
    footer: 'Hai domande? Torna a casa.',
  },
  // Footer
  footer: {
    legal: 'Avviso legale',
    privacy: 'Politica sulla privacy',
    cookies: 'Politica sui cookie',
    terms: 'Termini e Condizioni',
  },
  // Pagina di avviso legale
  legalPage: {
    title: 'Avviso Legale',
    subtitle: 'Informazioni legali su KaraoNow',
    editor: {
      title: 'Editore del sito',
      name: 'axialdata',
      address: 'Impasse du pélican, 97233 Schoelcher, Martinica',
      phone: 'Telefono: +596696367741',
      email: 'Email: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: 'Direttore della pubblicazione',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'Hosting',
    },
    dataProtection: {
      title: 'Protezione dei dati',
      description: 'Per ulteriori informazioni sulla protezione dei dati personali, consultare la nostra informativa sulla privacy.',
      link: 'Leggi la nostra politica sulla privacy',
    },
  },
  // Pagina di politica sulla privacy
  privacyPage: {
    title: 'Politica Sulla Privacy',
    subtitle: 'Come proteggiamo i dati personali',
    lastUpdated: 'Ultimo aggiornamento: 1º gennaio 2025',
    intro: {
      title: 'Introduzione',
      description: 'KaraoNow è impegnato nel proteggere la tua privacy. Questa politica spiega come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali in conformità al GDPR.',
    },
    dataCollection: {
      title: 'Dati raccolti',
      organizers: {
        title: 'Per gli organizzatori',
        email: 'Indirizzo email',
        name: 'Nome, cognome e organizzazione',
        organization: 'Dettagli dell\'organizzazione',
        payment: 'Informazioni di pagamento (tramite processore di terze parti)',
        events: 'Informazioni sugli eventi creati',
      },
      singers: {
        title: 'Per i cantanti',
        email: 'Indirizzo email (facoltativo)',
        pseudo: 'Pseudonimo utente',
        name: 'Nome e cognome (facoltativo)',
        participation: 'Eventi a cui hai partecipato',
        history: 'Cronologia delle canzoni aggiunte alla coda',
      },
    },
    purposes: {
      title: 'Uso dei tuoi dati',
      authentication: 'Autenticazione e gestione dell\'account',
      eventManagement: 'Creazione e gestione degli eventi',
      songManagement: 'Gestione delle code di canzoni',
      communication: 'Comunicazioni importanti riguardanti il tuo account',
      billing: 'Elaborazione dei pagamenti e fatturazione',
      support: 'Assistenza clienti e supporto tecnico',
      preferences: 'Miglioramento della tua esperienza utente',
    },
    legalBasis: {
      title: 'Base legale del trattamento',
      description: 'Il trattamento dei tuoi dati si basa sul tuo consenso al momento della registrazione e sull\'esecuzione del contratto di servizio tra te e KaraoNow.',
    },
    retention: {
      title: 'Periodo di conservazione dei dati',
      active: 'Dati attivi: conservati finché il tuo account è attivo',
      inactive: 'Dati inattivi: eliminati 12 mesi dopo l\'ultimo accesso',
      legal: 'Dati legali: conservati finché richiesto dalla legge',
    },
    rights: {
      title: 'I tuoi diritti GDPR',
      description: 'Secondo il GDPR, hai i seguenti diritti:',
      access: 'Diritto di accesso ai tuoi dati personali',
      rectification: 'Diritto di rettifica dei tuoi dati',
      deletion: 'Diritto all\'oblio (eliminazione dei dati)',
      portability: 'Diritto alla portabilità dei dati',
      opposition: 'Diritto di opposizione al trattamento',
      limitation: 'Diritto di limitare il trattamento',
      contact: 'Per esercitare questi diritti, contattaci a support@karaonow.com',
    },
    security: {
      title: 'Sicurezza',
      description: 'Utilizziamo misure di sicurezza appropriate per proteggere i tuoi dati da accessi non autorizzati, modifiche o divulgazioni.',
    },
    cookies: {
      title: 'Cookie',
      description: 'KaraoNow utilizza i cookie per migliorare la tua esperienza utente. Per ulteriori informazioni, consulta la nostra politica sui cookie.',
      link: 'Leggi la nostra politica sui cookie',
    },
    contact: {
      title: 'Contattaci',
      description: 'Per qualsiasi domanda su questa politica o sui tuoi dati personali, contattaci:',
      email: 'support@karaonow.com',
    },
  },
  // Pagina dei termini di servizio
  termsPage: {
    title: 'Termini e Condizioni',
    subtitle: 'Termini e Condizioni di KaraoNow',
    lastUpdated: 'Ultimo aggiornamento: 1º gennaio 2025',
    intro: {
      title: 'Introduzione',
      description: 'Benvenuto su KaraoNow! Utilizzando la nostra piattaforma, accetti questi termini e condizioni. Si prega di leggerli attentamente.',
    },
    service: {
      title: 'Descrizione del servizio',
      description: 'KaraoNow è una piattaforma SaaS per gestire eventi di karaoke. La piattaforma offre due interfacce principali:',
      organizers: {
        title: 'Per gli organizzatori',
        description: 'Gli organizzatori possono creare eventi, generare codici QR da condividere con i cantanti, gestire le code di canzoni e modificare l\'ordine di turno. Un sistema di abbonamento offre tre piani: gratuito (limitato), evento singolo e premium (abbonamento mensile).',
      },
      singers: {
        title: 'Per i cantanti',
        description: 'I cantanti possono unirsi agli eventi tramite codice QR, aggiungere canzoni alla coda dell\'evento e visualizzare l\'ordine delle canzoni. I cantanti possono partecipare senza un account (modalità ospite) o con un account per funzioni avanzate.',
      },
    },
    accounts: {
      title: 'Account utente',
      description: 'Per utilizzare KaraoNow, devi creare un account. Sei responsabile del mantenimento della riservatezza delle tue credenziali.',
      responsibility: 'Sei responsabile di tutte le attività eseguite tramite il tuo account',
      accuracy: 'Accetti di fornire informazioni accurate e aggiornate',
      security: 'Accetti di mantenere la sicurezza della tua password',
      notTransferable: 'Il tuo account è personale e non trasferibile',
    },
    subscription: {
      title: 'Piani di abbonamento',
      description: 'KaraoNow offre diversi piani di abbonamento per gli organizzatori:',
      free: 'Piano gratuito: Massimo 4 eventi (tutti gli stati combinati, esclusi gli eventi unici), 15 canzoni per evento',
      oneShot: 'Evento unico: Pagamento unico per un evento specifico, canzoni illimitate, periodo di validità limitato',
      premium: 'Piani a pagamento con abbonamento mensile: Diversi piani disponibili con quote mensili di eventi, canzoni illimitate per evento, rinnovo automatico mensile',
    },
    payment: {
      title: 'Termini di pagamento',
      description: 'Il pagamento viene elaborato tramite un processore di pagamento di terze parti sicuro.',
      currency: 'I pagamenti avvengono in euro (EUR)',
      noRefund: 'Nessun rimborso viene concesso per servizi già forniti',
      cancellation: 'Puoi annullare il tuo abbonamento in qualsiasi momento',
      automaticRenewal: 'Gli abbonamenti mensili si rinnovano automaticamente ogni mese se non annullati',
    },
    conduct: {
      title: 'Comportamento degli utenti',
      description: 'Accetti di utilizzare KaraoNow in modo legale ed etico.',
      prohibited: {
        title: 'Azioni vietate',
        illegal: 'Qualsiasi attività illegale o violazione delle leggi applicabili',
        harmful: 'Contenuto dannoso, offensivo o diffamatorio',
        harassing: 'Molestie, intimidazione o discriminazione',
        spam: 'Invio di spam o altre comunicazioni indesiderate',
        fraud: 'Tentativi di frode o truffe',
      },
    },
    organizerResponsibilities: {
      title: 'Responsabilità dell\'organizzatore',
      description: 'Gli organizzatori accettano le seguenti responsabilità:',
      songSelection: 'Sei responsabile della legalità dei contenuti delle canzoni',
      eventManagement: 'Devi gestire il tuo evento in conformità con questi termini',
      dataManagement: 'Sei responsabile della gestione dei dati dei cantanti',
      copyrightCompliance: 'Accetti di rispettare le leggi sul copyright per le canzoni',
    },
    singerRights: {
      title: 'Diritti e responsabilità del cantante',
      description: 'I cantanti hanno i seguenti diritti e accettano le seguenti responsabilità:',
      participation: 'Puoi partecipare a eventi organizzati da altri',
      songSelection: 'Puoi aggiungere canzoni alla coda dell\'evento',
      noDuplication: 'Non puoi aggiungere la stessa canzone più volte',
      respect: 'Accetti di rispettare le regole stabilite dall\'organizzatore',
    },
    intellectualProperty: {
      title: 'Proprietà intellettuale',
      description: 'Tutti i diritti di proprietà intellettuale su KaraoNow e il suo contenuto (eccetto le canzoni musicali) appartengono a KaraoNow. Non puoi riprodurre o utilizzare il contenuto senza autorizzazione.',
    },
    liability: {
      title: 'Limitazione di responsabilità',
      description: 'KaraoNow è fornito "così com\'è". Non siamo responsabili dei danni diretti o indiretti derivanti dall\'uso della piattaforma. La nostra responsabilità è limitata al massimo all\'importo che hai pagato per il tuo abbonamento nell\'ultimo mese.',
    },
    termination: {
      title: 'Risoluzione',
      description: 'KaraoNow si riserva il diritto di sospendere o terminare il tuo account in caso di violazione di questi termini. Puoi terminare il tuo account in qualsiasi momento.',
    },
    modifications: {
      title: 'Modifiche ai termini',
      description: 'KaraoNow si riserva il diritto di modificare questi termini in qualsiasi momento. Le modifiche entrano in vigore 30 giorni dopo la notifica. Il tuo uso continuato della piattaforma dopo questo periodo significa la tua accettazione dei nuovi termini.',
    },
    jurisdiction: {
      title: 'Giurisdizione',
      description: 'Questi termini sono disciplinati dalla legge francese. Qualsiasi controversia sarà soggetta ai tribunali competenti in Francia.',
    },
    contact: {
      title: 'Contatti',
      description: 'Per qualsiasi domanda su questi termini, contattaci:',
      email: 'support@karaonow.com',
    },
  },
}
